// Smart Daara - Student Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { progressAPI, courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  BookOpen, Play, CheckCircle, Clock, Trophy, ArrowRight, 
  BarChart3, BookMarked, Star
} from 'lucide-react';

export default function StudentDashboard() {
  const { user } = useAuth();
  const { t, language, getText } = useLanguage();
  const navigate = useNavigate();
  
  const [myCourses, setMyCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    totalLessonsCompleted: 0
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      // Load enrolled courses
      const enrolledResponse = await progressAPI.getMyCourses();
      const enrolled = enrolledResponse.data || [];
      setMyCourses(enrolled);

      // Calculate stats
      const completed = enrolled.filter(c => c.progress_percentage >= 100).length;
      const inProgress = enrolled.filter(c => c.progress_percentage > 0 && c.progress_percentage < 100).length;
      const lessonsCompleted = enrolled.reduce((sum, c) => sum + (c.lessons_completed || 0), 0);

      setStats({
        totalCourses: enrolled.length,
        completedCourses: completed,
        inProgressCourses: inProgress,
        totalLessonsCompleted: lessonsCompleted
      });

      // Load available courses
      const coursesResponse = await courseAPI.listCourses({ is_published: true });
      setAllCourses((coursesResponse.data || []).slice(0, 6));
    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {t('welcome')}, {user?.first_name}!
          </h1>
          <p className="text-slate-500">
            {language === 'fr' ? 'Continuez votre apprentissage' : 'Continue your learning journey'}
          </p>
        </div>
        <Button 
          className="bg-emerald-700 hover:bg-emerald-800"
          onClick={() => navigate('/student/catalog')}
        >
          <BookOpen className="w-4 h-4 mr-2" />
          {t('courseCatalog')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <BookMarked className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{stats.totalCourses}</p>
                <p className="text-xs text-slate-500">{language === 'fr' ? 'Cours Inscrits' : 'Enrolled Courses'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Play className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{stats.inProgressCourses}</p>
                <p className="text-xs text-slate-500">{t('inProgress')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{stats.completedCourses}</p>
                <p className="text-xs text-slate-500">{t('completed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Trophy className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{stats.totalLessonsCompleted}</p>
                <p className="text-xs text-slate-500">{language === 'fr' ? 'Leçons Terminées' : 'Lessons Completed'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Courses Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-outfit">{t('myCourses')}</CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Continuez là où vous vous êtes arrêté' : 'Continue where you left off'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/my-courses')}>
            {language === 'fr' ? 'Voir tout' : 'View all'}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {myCourses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCourses.slice(0, 3).map((course) => (
                <div
                  key={course.id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => navigate(`/student/course/${course.course_id}`)}
                  data-testid={`course-card-${course.course_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-emerald-700" />
                    </div>
                    {course.progress_percentage >= 100 && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {t('completed')}
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-slate-900 mb-1 truncate">
                    {course.course_title_fr || course.course_title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3">
                    {course.lessons_completed || 0}/{course.total_lessons || 0} {t('lessons')}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('progress')}</span>
                      <span className="font-medium text-emerald-700">
                        {Math.round(course.progress_percentage || 0)}%
                      </span>
                    </div>
                    <Progress value={course.progress_percentage || 0} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookMarked className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 mb-4">
                {language === 'fr' 
                  ? "Vous n'êtes inscrit à aucun cours" 
                  : "You're not enrolled in any courses yet"}
              </p>
              <Button onClick={() => navigate('/student/catalog')}>
                {language === 'fr' ? 'Parcourir le catalogue' : 'Browse catalog'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Courses */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-outfit">{t('courseCatalog')}</CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Découvrez de nouveaux cours' : 'Discover new courses'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/catalog')}>
            {language === 'fr' ? 'Voir tout' : 'View all'}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allCourses.map((course) => (
              <div
                key={course.id}
                className="p-4 border border-slate-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => navigate(`/student/course/${course.id}`)}
              >
                <div className="w-full h-32 bg-gradient-to-br from-emerald-100 to-amber-50 rounded-lg mb-3 flex items-center justify-center">
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <BookOpen className="w-12 h-12 text-emerald-600" />
                  )}
                </div>
                <h3 className="font-medium text-slate-900 mb-1 truncate">
                  {getText(course, 'title')}
                </h3>
                <p className="text-sm text-slate-500 mb-2 line-clamp-2">
                  {getText(course, 'description') || (language === 'fr' ? 'Aucune description' : 'No description')}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {course.total_lessons || 0} {t('lessons')}
                  </span>
                  <Button size="sm" variant="ghost" className="text-emerald-700">
                    {t('startCourse')}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
